<template>
	<div
		:class="[
			'form-group',
			{
				filled: value && value.toString().length > 0,
				focused,
				disabled,
			},
		]"
	>
		<div
			v-if="!omitLabel"
			class="field-label-container"
		>
			<div
				v-if="!omitInfo"
				class="info-anchor"
			>
				<button
					ref="tooltipTrigger"
					class="info-button"
					:class="{
						active: $slots.tooltip,
					}"
					type="button"
					:disabled="disabled"
					@click.prevent="ToggleToolTip"
				>
					<i class="fal fa-info-circle mr-2 tooltip-icon"></i>
				</button>

				<div
					v-show="showToolTip"
					ref="tooltipPopup"
					class="info-inline-popup"
				>
					<slot name="tooltip"></slot>
				</div>
			</div>
			<label class="field-label">
				{{ label }}
				<span
					v-if="required"
					class="required-mark"
				>
					*
				</span>
			</label>
		</div>
		<template v-if="inputType === 'text'">
			<div class="input-wrapper gap-3">
				<input
					ref="input"
					:value="value"
					type="text"
					class="form-control"
					:class="resolvedInputClasses"
					:placeholder="placeholder"
					:disabled="disabled"
					:readonly="readonly"
					:maxlength="maxLength"
					@focus="focused = true"
					@blur="
						$emit('blur', $event)
						focused = false
					"
					@input="HandleTextInput"
				/>
				<i
					v-if="value?.length && !readonly && !disabled"
					class="clear-icon fas fa-times text-2xl leading-none"
					@click.stop.prevent="Clear('text')"
				></i>
				<div
					v-if="readonly"
					class="readonly-overlay"
				></div>
			</div>
		</template>
		<template v-if="inputType === 'textarea'">
			<div class="input-wrapper gap-3">
				<textarea
					ref="input"
					:value="value"
					class="form-control"
					:class="resolvedInputClasses"
					:placeholder="placeholder"
					:disabled="disabled"
					:maxlength="maxLength"
					:readonly="readonly"
					@focus="focused = true"
					@blur="focused = false"
					@input="HandleTextInput"
				></textarea>
				<div
					v-if="readonly"
					class="readonly-overlay"
				></div>
			</div>
		</template>
		<template v-else-if="inputType === 'date'">
			<div :class="['date-input-wrapper', { disabled }, resolvedInputClasses]">
				<i class="far fa-calendar-alt date-icon"></i>
				<flatpickr
					ref="input"
					v-model="dateValue"
					:config="datePickerConfig"
					class="form-control"
					:placeholder="placeholder"
					:disabled="disabled"
					@on-open="focused = true"
					@on-close="focused = false"
				/>
				<i
					v-if="value?.length && !readonly && !disabled"
					class="clear-icon fas fa-times text-2xl leading-none"
					@click.stop.prevent="Clear('date')"
				></i>
			</div>
		</template>
		<template v-else-if="inputType === 'checkbox'">
			<div class="checkbox-input-wrapper">
				<input
					:id="`checkbox-${entityKey}`"
					:checked="Boolean(value)"
					type="checkbox"
					class="custom-checkbox"
					:class="resolvedInputClasses"
					:disabled="disabled"
					@change="$emit('input', $event.target.checked)"
				/>
				<label
					class="field-label"
					:for="`checkbox-${entityKey}`"
				>
					{{ checkboxLabel }}
				</label>
			</div>
		</template>
		<template v-else-if="inputType === 'radio'">
			<div class="radio-group-wrapper">
				<template v-if="options && options.length">
					<div
						v-for="(option, index) in options"
						:key="index"
						class="radio-input-wrapper"
					>
						<input
							:id="`radio-${radioGroupName}-${index}`"
							:name="radioGroupName"
							:checked="IsRadioSelected(option)"
							:value="GetOptionValue(option)"
							type="radio"
							class="custom-radio"
							:class="resolvedInputClasses"
							:disabled="disabled || readonly"
							@change="SelectRadio(option)"
						/>
						<label
							class="field-label"
							:for="`radio-${radioGroupName}-${index}`"
						>
							{{ GetOptionLabel(option) }}
						</label>
					</div>
				</template>
				<template v-else>
					<div class="radio-input-wrapper">
						<input
							:id="`radio-${entityKey || uniqueId}`"
							:name="radioGroupName"
							:checked="Boolean(value)"
							type="radio"
							class="custom-radio"
							:class="resolvedInputClasses"
							:disabled="disabled || readonly"
							@change="$emit('input', $event.target.checked)"
						/>
						<label
							class="field-label"
							:for="`radio-${entityKey || uniqueId}`"
						>
							{{ checkboxLabel || label }}
						</label>
					</div>
				</template>
			</div>
		</template>
		<template v-else-if="inputType === 'select'">
			<Autocomplete
				:value="value"
				:class="resolvedInputClasses"
				:options="options"
				:placeholder="placeholder"
				:min-length="minLength"
				:disabled="disabled"
				:readonly="readonly"
				:input-classes="inputClasses"
				:intercept-select="interceptSelect"
				@select="$emit('select', $event)"
				@focus="focused = true"
				@blur="focused = false"
				@input="$emit('input', $event)"
			>
				<template #buttons>
					<slot name="buttons"></slot>
				</template>
				<template #option="{ option }">
					<slot
						name="option"
						:option="option"
					></slot>
				</template>
			</Autocomplete>
		</template>
		<template v-else-if="inputType === 'doc-select'">
			<DocAutocomplete
				:value="value"
				:class="resolvedInputClasses"
				:options="options"
				:placeholder="placeholder"
				:disabled="disabled"
				:readonly="readonly"
				:input-classes="inputClasses"
				:entity-key="entityKey"
				:formatter="formatter"
				@select="$emit('select', $event)"
				@focus="focused = true"
				@blur="focused = false"
				@input="$emit('input', $event)"
			/>
		</template>
		<template v-else-if="inputType === 'name-select'">
			<NameAutocomplete
				:value="value"
				:class="resolvedInputClasses"
				:options="options"
				:placeholder="placeholder"
				:disabled="disabled"
				:readonly="readonly"
				:input-classes="inputClasses"
				@select="$emit('select', $event)"
				@focus="focused = true"
				@blur="focused = false"
				@input="$emit('input', $event)"
			/>
		</template>
	</div>
</template>

<script>
	import moment from 'moment'
	import { createPopper } from '@popperjs/core'
	import Flatpickr from 'vue-flatpickr-component'
	import 'flatpickr/dist/flatpickr.css'
	import { Portuguese } from 'flatpickr/dist/l10n/pt.js'

	import { useGetUniqueId } from '@/composables'
	import NameAutocomplete from '@/components/shared/NameAutocomplete'

	export default {
		name: 'FormGroup',
		components: {
			Flatpickr,
			NameAutocomplete,
		},
		props: {
			value: {
				type: [String, Number, Boolean, Object],
				default: '',
			},
			inputType: {
				type: String,
				default: 'text',
			},
			name: {
				type: String,
				default: '',
			},
			inputClasses: {
				type: Array,
				default: () => [],
			},
			label: {
				type: String,
				default: '',
			},
			checkboxLabel: {
				type: String,
				default: '',
			},
			options: {
				type: Array,
				default: () => [],
			},
			interceptSelect: {
				type: Boolean,
				default: false,
			},
			required: {
				type: Boolean,
				default: false,
			},
			disabled: {
				type: Boolean,
				default: false,
			},
			readonly: {
				type: Boolean,
				default: false,
			},
			placeholder: {
				type: String,
				default: 'SELECIONE',
			},
			minLength: {
				type: [Number, String],
				default: 0,
			},
			maxLength: {
				type: [Number],
				default: null,
			},
			formatter: {
				type: String,
				default: '',
			},
			entityKey: {
				type: [String, Number],
				default: '',
			},
			omitLabel: {
				type: Boolean,
				default: false,
			},
			omitInfo: {
				type: Boolean,
				default: false,
			},
			limitedDate: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				uniqueId: null,
				datePickerConfig: null,
				showToolTip: false,
				tooltipPopper: null,
				focused: false,
			}
		},
		computed: {
			resolvedInputClasses() {
				return [this.inputClasses].flat(Infinity).filter(Boolean)
			},
			inputClassesString() {
				const classes = []

				for (const item of this.resolvedInputClasses) {
					if (typeof item === 'string') {
						classes.push(item)
					} else if (item && typeof item === 'object') {
						for (const [key, value] of Object.entries(item)) {
							if (value) classes.push(key)
						}
					}
				}

				return classes.join(' ')
			},
			radioGroupName() {
				return this.name || `radio-group-${this.entityKey || this.uniqueId}`
			},
			dateValue: {
				get() {
					return this.value
				},
				set(newValue) {
					this.$emit('input', newValue)
				},
			},
		},
		watch: {
			showToolTip(value) {
				if (value) {
					this.$nextTick(() => {
						this.CreateTooltipPopper()

						this.tooltipPopper?.update()
					})

					return
				}

				this.DestroyTooltipPopper()
			},
			resolvedInputClasses: {
				handler() {
					if (this.inputType !== 'date') return
					this.$nextTick(() => {
						const fp = this.$refs.input?.fp
						if (fp?.altInput) {
							fp.altInput.className = [
								'form-control',
								'flatpickr-alt-input',
								this.inputClassesString,
							].join(' ')
						}
					})
				},
				immediate: true,
			},
		},
		methods: {
			ToggleToolTip() {
				if (this.disabled || !this.$slots.tooltip) {
					return
				}

				this.showToolTip = !this.showToolTip
			},
			CreateTooltipPopper() {
				if (this.tooltipPopper || !this.$refs.tooltipTrigger || !this.$refs.tooltipPopup) {
					return
				}

				this.tooltipPopper = createPopper(this.$refs.tooltipTrigger, this.$refs.tooltipPopup, {
					placement: 'bottom-start',
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 0],
							},
						},
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['top-start'],
								padding: 8,
							},
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 8,
							},
						},
					],
				})
			},
			DestroyTooltipPopper() {
				if (!this.tooltipPopper) {
					return
				}

				this.tooltipPopper.destroy()
				this.tooltipPopper = null
			},
			CloseToolTip() {
				this.showToolTip = false
			},
			HandleTextInput(event) {
				const formattedValue = this.FormatInputValue(event.target.value)
				event.target.value = formattedValue
				this.$emit('input', formattedValue)
			},
			FormatInputValue(value) {
				const rawValue = String(value ?? '')

				switch (this.formatter) {
					case 'alphanumeric':
						return rawValue.replace(/[^a-zA-Z0-9À-ÿ\s]/g, '')
					case 'letters':
						return rawValue.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
					case 'numbers':
						return rawValue.replace(/[^0-9]/g, '')
					case 'cpf': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 11)
						return digits
							.replace(/^(\d{3})(\d)/, '$1.$2')
							.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
							.replace(/\.(\d{3})(\d)/, '.$1-$2')
					}
					case 'cnpj': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 14)
						return digits
							.replace(/^(\d{2})(\d)/, '$1.$2')
							.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
							.replace(/\.(\d{3})(\d)/, '.$1/$2')
							.replace(/(\d{4})(\d)/, '$1-$2')
					}
					case 'phone': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 11)
						if (digits.length <= 10) {
							return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
						}
						return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
					}
					case 'cep': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 8)
						return digits.replace(/^(\d{5})(\d)/, '$1-$2')
					}
					case 'email':
						return rawValue.replace(/[^a-zA-Z0-9@._-]/g, '')
					case 'factor': {
						const digits = rawValue.replace(/\D/g, '').slice(-3)
						if (!digits) return ''
						const cents = parseInt(digits, 10)
						if (cents === 0) return ''
						return (cents / 100).toFixed(2).replace('.', ',')
					}
					default:
						return rawValue
				}
			},
			HandleClick(event) {
				if (!this.showToolTip) {
					return
				}
				const popup = this.$refs.tooltipPopup
				const trigger = this.$refs.tooltipTrigger
				const path = event.composedPath()
				if ((popup && path.includes(popup)) || (trigger && path.includes(trigger))) {
					return
				}
				this.CloseToolTip()
			},
			Clear(type) {
				if (type === 'text') {
					this.$emit('input', '')
					this.$refs.input?.focus()
				} else if (type === 'date') {
					const fp = this.$refs.input?.fp
					fp?.clear()
					fp?.open()
					fp?.altInput?.focus()
					fp?.jumpToDate(new Date())
				}
			},
			GetOptionValue(option) {
				if (typeof option === 'object' && option !== null) {
					return option.value ?? option.id ?? option.key ?? option.label ?? option
				}
				return option
			},
			GetOptionLabel(option) {
				if (typeof option === 'object' && option !== null) {
					return option.label ?? option.text ?? option.name ?? option.title ?? option.value ?? ''
				}
				return String(option)
			},
			IsRadioSelected(option) {
				const optionVal = this.GetOptionValue(option)
				if (typeof this.value === 'object' && this.value !== null) {
					return this.GetOptionValue(this.value) === optionVal
				}
				return this.value === optionVal
			},
			SelectRadio(option) {
				const optionVal = this.GetOptionValue(option)
				this.$emit('input', optionVal)
				this.$emit('change', optionVal)
				this.$emit('select', option)
			},
			SetupDatePickerConfig() {
				if (this.inputType !== 'date') return

				this.datePickerConfig = {
					allowInput: true,
					dateFormat: 'Y-m-d',
					disableMobile: true,
					altFormat: 'd/m/Y',
					altInput: true,
					altInputClass: 'form-control flatpickr-alt-input',
					locale: Portuguese,
					parseDate(datestr, format) {
						if (!datestr) return null
						if (datestr instanceof Date) return datestr

						if (typeof datestr === 'string') {
							// Parse dd/mm/yyyy typed by user
							if (datestr.includes('/')) {
								const parts = datestr.split('/')
								if (parts.length === 3) {
									const d = parseInt(parts[0], 10)
									const m = parseInt(parts[1], 10) - 1
									const y = parseInt(parts[2], 10)
									if (!isNaN(d) && !isNaN(m) && !isNaN(y) && parts[2].length === 4) {
										return new Date(y, m, d, 0, 0, 0)
									}
								}
							}

							// Parse YYYY-MM-DD (ou YYYY-MM-DDTHH:mm:ss) desconsiderando deslocamento de fuso horário (UTC)
							if (datestr.includes('-')) {
								const datePart = datestr.split('T')[0]
								const parts = datePart.split('-')
								if (parts.length === 3) {
									const y = parseInt(parts[0], 10)
									const m = parseInt(parts[1], 10) - 1
									const d = parseInt(parts[2], 10)
									if (!isNaN(d) && !isNaN(m) && !isNaN(y) && parts[0].length === 4) {
										return new Date(y, m, d, 0, 0, 0)
									}
								}
							}
						}

						return new Date(datestr)
					},
					onReady(selectedDates, dateStr, instance) {
						// Date mask on alt input
						const altInput = instance.altInput
						if (altInput) {
							altInput.maxLength = 10
							altInput.addEventListener('input', function () {
								const digits = this.value.replace(/\D/g, '').slice(0, 8)
								let formattedValue = digits

								if (digits.length > 4) {
									formattedValue =
										digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4)
								} else if (digits.length > 2) {
									formattedValue = digits.slice(0, 2) + '/' + digits.slice(2)
								}

								altInput.value = formattedValue

								if (digits.length !== 8) {
									altInput.setCustomValidity('Informe uma data válida.')
									return
								}

								const day = Number(digits.slice(0, 2))
								const month = Number(digits.slice(2, 4)) - 1
								const year = Number(digits.slice(4))
								const date = new Date(year, month, day)
								const isValidDate =
									date.getFullYear() === year && date.getMonth() === month && date.getDate() === day

								altInput.setCustomValidity(isValidDate ? '' : 'Informe uma data válida.')
							})
						}

						// Clear button
						if (!instance.calendarContainer.querySelector('.flatpickr-clear-container')) {
							const container = document.createElement('div')
							container.className = 'flatpickr-clear-container'
							const clearBtn = document.createElement('button')
							clearBtn.textContent = 'LIMPAR'
							clearBtn.type = 'button'
							clearBtn.className = 'flatpickr-clear btn btn-sm btn-light'
							clearBtn.onclick = function () {
								instance.clear()
								instance.close()
							}
							container.appendChild(clearBtn)
							instance.calendarContainer.appendChild(container)
						}
					},
				}

				if (this.limitedDate) {
					const minDate = moment().subtract(1, 'month').startOf('month').toDate()
					this.datePickerConfig.minDate = minDate
				}
			},
		},
		beforeMount() {
			this.SetupDatePickerConfig()
		},
		mounted() {
			this.uniqueId = useGetUniqueId('form-group')
			window.addEventListener('click', this.HandleClick)
		},
		beforeDestroy() {
			this.DestroyTooltipPopper()
			window.removeEventListener('click', this.HandleClick)
		},
		beforeUnmount() {
			this.DestroyTooltipPopper()
			window.removeEventListener('click', this.HandleClick)
		},
	}
</script>

<style lang="scss" scoped>
	.form-group {
		--inputBackgroundColor: #223042;
		--inputBorderColor: #314977;
		--iconColor: #4a5b74;

		.field-label {
			display: flex;
			align-items: center;
			text-transform: uppercase;
			font-size: 0.875rem;
			font-weight: 600;
			color: var(--newPrimaryInitialBackgroundColor);
			margin-bottom: 6px;
			white-space: nowrap;
			padding: 0;
		}

		.input-wrapper {
			position: relative;

			i {
				position: absolute;
				top: 50%;
				transform: translateY(-50%);
				z-index: 5;

				&.clear-icon {
					right: 0.2rem;
					color: var(--newPrimaryBackgroundColor);
					cursor: pointer;
					padding: 0.25rem;

					&:hover {
						color: var(--newPrimaryFocusColor);
					}
				}
			}

			input.form-control[type='email'] {
				text-transform: lowercase;

				&::placeholder {
					text-transform: uppercase;
				}
			}

			textarea.form-control {
				padding: 0.5rem 2rem 0.5rem 0.5rem;
				height: 5.125rem;
				resize: none;
			}

			.readonly-overlay {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				cursor: not-allowed;
			}
		}

		.date-input-wrapper {
			position: relative;

			i {
				position: absolute;
				top: 50%;
				transform: translateY(-50%);
				z-index: 5;

				&.date-icon {
					left: 0.5rem;
					transform: translateY(-50%);
					color: var(--iconColor);
					pointer-events: none;
					font-size: 0.8rem;
				}

				&.clear-icon {
					right: 0.2rem;
					color: var(--newPrimaryBackgroundColor);
					cursor: pointer;
					padding: 0.25rem;

					&:hover {
						color: var(--newPrimaryFocusColor);
					}
				}
			}

			.form-control,
			:deep(.form-control) {
				padding: 0 1.5rem;
			}

			.flatpickr-alt-input {
				text-transform: uppercase;
				font-size: 0.875rem;

				&::placeholder {
					color: var(--fontColorActiveNotSelected);
					opacity: 1;
				}
			}

			&.disabled {
				:deep(input.form-control:disabled) {
					padding: 0 1.5rem;
				}

				i.date-icon {
					color: var(--disabledColor);
				}
			}
		}

		.required-mark {
			color: #eab308;
			margin-left: 2px;
		}

		.info-button {
			padding: 0;
			border: none;
			background-color: transparent;
			cursor: pointer;

			i {
				font-size: 1.25rem;
				margin-top: 0.3rem;
				color: #334155;
			}

			&.active {
				i {
					color: var(--newPrimaryFocusBackgroundColor);
				}

				&:hover {
					i {
						color: var(--fontColorHover);
					}
				}
			}

			&:disabled {
				cursor: default;

				i {
					color: var(--disabledColor);
				}
			}
		}

		.field-label-container {
			display: flex;
			align-items: center;
			gap: 0.2rem;

			.field-label {
				min-height: 28px;
				margin-bottom: 0;
			}

			.info-anchor {
				position: relative;
				display: inline-flex;
				align-items: center;
			}

			.info-inline-popup {
				width: 380px;
				max-width: calc(100vw - 16px);
				position: absolute;
				top: 0;
				left: 0;
				z-index: 1000;
				background-color: var(--tippyBackgroundColor);
				border: 1px solid var(--tippyBorderColor);
				border-radius: 0.5rem;
				box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
				white-space: normal;
				padding: 0.75rem;

				.info-modal-list {
					margin: 0;
					padding: 0;
					list-style: none;

					.info-modal-item {
						padding-top: 0.25rem;
						padding-bottom: 0.25rem;
						color: var(--fontColor);
						font-size: 0.875rem;
					}
				}
			}
		}

		.checkbox-input-wrapper {
			display: flex;
			align-items: center;
			gap: 0.5rem;

			.custom-checkbox {
				-webkit-appearance: none;
				appearance: none;
				width: 2.25rem;
				height: 2.25rem;
				cursor: pointer;
				border-radius: 4px;
				background-color: var(--inputBackgroundColor);
				border: 1px solid var(--inputBorderColor);
				position: relative;
				transition: background-color 0.2s, border-color 0.2s;

				&:checked {
					background-color: var(--backgroundColorActiveSelected);
					border-color: var(--newColor);
				}

				&:checked::after {
					content: '';
					position: absolute;
					top: 40%;
					left: 50%;
					width: 12px;
					height: 22px;
					border: solid var(--fontColorActiveSelected);
					border-width: 0 3px 3px 0;
					transform: translate(-50%, -50%) rotate(45deg);
				}

				&:disabled {
					cursor: not-allowed;
					opacity: 0.6;
				}
			}

			label.field-label {
				margin-bottom: 0;
				font-size: 0.75rem;
				cursor: pointer;
			}
		}

		.radio-group-wrapper {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: 0.75rem 1.5rem;
		}

		.radio-input-wrapper {
			display: flex;
			align-items: center;
			gap: 0.5rem;

			.custom-radio {
				-webkit-appearance: none;
				appearance: none;
				width: 1rem;
				height: 1rem;
				cursor: pointer;
				border-radius: 50%;
				background-color: var(--inputBackgroundColor);
				border: 1px solid var(--inputBorderColor);
				position: relative;
				flex-shrink: 0;
				transition: background-color 0.2s, border-color 0.2s;

				&:checked {
					background-color: var(--backgroundColorActiveSelected);
					border-color: var(--newColor);
				}

				&:disabled {
					cursor: not-allowed;
					opacity: 0.6;
				}
			}

			label.field-label {
				margin-bottom: 0;
				font-size: 0.75rem;
				cursor: pointer;
				user-select: none;
			}
		}

		&.filled,
		&.focused {
			.form-control:not(:read-only) {
				background-color: var(--newPrimaryFocusBackgroundColor);
				color: var(--newPrimaryFocusColor);
				outline: none;
				caret-color: var(--newPrimaryFocusColor);
				padding-right: 1.8rem;

				&:disabled {
					color: var(--disabledColor);

					&::placeholder {
						color: var(--disabledColor);
					}
				}
			}

			.date-input-wrapper {
				:deep(.flatpickr-alt-input) {
					background-color: var(--newPrimaryFocusBackgroundColor);
					color: var(--newPrimaryFocusColor);
					outline: none;
					caret-color: var(--newPrimaryFocusColor);
				}

				i.date-icon {
					color: var(--newPrimaryFocusColor);
				}
			}

			.field-label {
				color: var(--newPrimaryFocusBackgroundColor);
			}

			.checkbox-input-wrapper,
			.radio-input-wrapper {
				label.field-label {
					font-size: 0.875rem;
				}
			}
		}

		&.disabled {
			.field-label {
				color: var(--disabledColor);
			}

			.custom-radio {
				cursor: not-allowed;
				opacity: 0.6;
			}
		}
	}
</style>
